// src/components/roadmap/JiraImportDialog.jsx
import React, { useEffect, useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "components/ui/dialog";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Input } from "components/ui/input";
import { Label } from "components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { Checkbox } from "components/ui/checkbox";
import { Skeleton } from "components/ui/skeleton";
import { ExternalLink, Filter, Download, AlertTriangle, CheckCircle } from "lucide-react";
import { fetchJiraInitiatives } from "../../adapters/jiraAdapter";

export default function JiraImportDialog({ open, onClose, onImport, jiraConfig }) {
  const [initiatives, setInitiatives] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [isImporting, setIsImporting] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    project: "all",
    type: "all",
    status: "all",
    horizon: "all",
    search: ""
  });

  // Load JIRA initiatives when dialog opens
  useEffect(() => {
    if (!open || !jiraConfig) return;

    const loadInitiatives = async () => {
      setIsLoading(true);
      setError(null);
      try {
        console.log('🔄 Loading JIRA initiatives...');
        const data = await fetchJiraInitiatives({ max: 100 });

        console.log('✅ Loaded initiatives:', data.length);
        setInitiatives(data);
      } catch (err) {
        console.error('❌ Failed to load JIRA initiatives:', err);
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    loadInitiatives();
  }, [open, jiraConfig]);

  // Filter and search initiatives
  const filteredInitiatives = useMemo(() => {
    return initiatives.filter(item => {
      const projectMatch = filters.project === "all" || item.jira_project === filters.project;
      const typeMatch = filters.type === "all" || item.jira_type === filters.type;
      const statusMatch = filters.status === "all" || item.status === filters.status;
      const horizonMatch = filters.horizon === "all" || item.time_horizon === filters.horizon;
      const searchMatch = !filters.search ||
        item.title.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.description.toLowerCase().includes(filters.search.toLowerCase()) ||
        item.jira_key.toLowerCase().includes(filters.search.toLowerCase());

      return projectMatch && typeMatch && statusMatch && horizonMatch && searchMatch;
    });
  }, [initiatives, filters]);

  // Get unique values for filter dropdowns
  const filterOptions = useMemo(() => {
    const projects = [...new Set(initiatives.map(i => i.jira_project).filter(Boolean))];
    const types = [...new Set(initiatives.map(i => i.jira_type).filter(Boolean))];
    const statuses = [...new Set(initiatives.map(i => i.status).filter(Boolean))];
    const horizons = [...new Set(initiatives.map(i => i.time_horizon).filter(Boolean))];

    return { projects, types, statuses, horizons };
  }, [initiatives]);

  const handleSelectAll = () => {
    if (selectedItems.length === filteredInitiatives.length) {
      setSelectedItems([]);
    } else {
      setSelectedItems(filteredInitiatives.map(item => item.jira_key));
    }
  };

  const handleSelectItem = (jiraKey) => {
    setSelectedItems(prev =>
      prev.includes(jiraKey)
        ? prev.filter(key => key !== jiraKey)
        : [...prev, jiraKey]
    );
  };

  const handleImport = async () => {
    if (selectedItems.length === 0) return;

    setIsImporting(true);
    try {
      const itemsToImport = initiatives.filter(item => selectedItems.includes(item.jira_key));
      console.log('🚀 Importing', itemsToImport.length, 'initiatives from JIRA');

      await onImport?.(itemsToImport);

      // Reset selection after successful import
      setSelectedItems([]);
      onClose();
    } catch (error) {
      console.error('❌ Import failed:', error);
      setError(`Import failed: ${error.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const statusBadgeStyle = (status) => {
    const styles = {
      planned: { bg: "var(--bg-subtle)", color: "var(--fg-secondary)" },
      in_progress: { bg: "var(--warning-soft)", color: "var(--warning-fg)" },
      done: { bg: "var(--success-soft)", color: "var(--success-fg)" }
    };
    return styles[status] || styles.planned;
  };

  const typeBadgeStyle = (type) => {
    const styles = {
      Epic: { bg: "var(--warning-soft)", color: "var(--warning-fg)" },
      Feature: { bg: "var(--accent-soft)", color: "var(--accent-fg)" },
      Initiative: { bg: "var(--accent-soft)", color: "var(--accent-fg)" }
    };
    return styles[type] || { bg: "var(--bg-subtle)", color: "var(--fg-secondary)" };
  };

  const horizonBadgeStyle = (horizon) => {
    const styles = {
      Now: { bg: "var(--success-soft)", color: "var(--success-fg)" },
      Next: { bg: "var(--accent-soft)", color: "var(--accent-fg)" },
      Later: { bg: "var(--bg-subtle)", color: "var(--fg-secondary)" }
    };
    return styles[horizon] || styles.Next;
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent style={{ maxWidth: "900px", maxHeight: "90vh" }}>
        <DialogHeader>
          <DialogTitle style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 18, fontWeight: 800 }}>
            <Download size={20} color="var(--accent)" />
            Import Initiatives from JIRA
          </DialogTitle>
          <p style={{ margin: 0, color: "var(--fg-muted)", fontSize: 14 }}>
            Select JIRA Epics, Features, and Initiatives to import into your roadmap
          </p>
        </DialogHeader>

        {error && (
          <div style={{
            padding: 12,
            background: "var(--danger-soft)",
            border: "1px solid var(--danger-border)",
            borderRadius: 8,
            display: "flex",
            alignItems: "center",
            gap: 8
          }}>
            <AlertTriangle size={16} color="var(--danger)" />
            <span style={{ color: "var(--danger)", fontSize: 14 }}>{error}</span>
          </div>
        )}

        {/* Filters */}
        <div style={{
          padding: 16,
          background: "var(--bg-inset)",
          borderRadius: 8,
          border: "1px solid var(--border)"
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            <Filter size={16} color="var(--fg-muted)" />
            <Label style={{ fontWeight: 600 }}>Filters</Label>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 12 }}>
            <div>
              <Label style={{ fontSize: 12 }}>Search</Label>
              <Input
                placeholder="Title, description, key..."
                value={filters.search}
                onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                style={{ marginTop: 4 }}
              />
            </div>

            <div>
              <Label style={{ fontSize: 12 }}>Project</Label>
              <Select value={filters.project} onValueChange={(v) => setFilters(prev => ({ ...prev, project: v }))}>
                <SelectTrigger style={{ marginTop: 4 }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Projects</SelectItem>
                  {filterOptions.projects.map(project => (
                    <SelectItem key={project} value={project}>{project}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label style={{ fontSize: 12 }}>Type</Label>
              <Select value={filters.type} onValueChange={(v) => setFilters(prev => ({ ...prev, type: v }))}>
                <SelectTrigger style={{ marginTop: 4 }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {filterOptions.types.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label style={{ fontSize: 12 }}>Status</Label>
              <Select value={filters.status} onValueChange={(v) => setFilters(prev => ({ ...prev, status: v }))}>
                <SelectTrigger style={{ marginTop: 4 }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  {filterOptions.statuses.map(status => (
                    <SelectItem key={status} value={status}>{status.replace('_', ' ')}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Results */}
        <div style={{ flex: 1, overflow: "hidden" }}>
          {isLoading ? (
            <div style={{ display: "grid", gap: 12 }}>
              {Array.from({ length: 3 }, (_, i) => (
                <Skeleton key={i} style={{ height: 80, width: "100%", borderRadius: 8 }} />
              ))}
            </div>
          ) : (
            <>
              {/* Selection header */}
              <div style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 0",
                borderBottom: "1px solid var(--border)"
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <Checkbox
                    checked={selectedItems.length === filteredInitiatives.length && filteredInitiatives.length > 0}
                    onCheckedChange={handleSelectAll}
                  />
                  <span style={{ fontSize: 14, fontWeight: 500 }}>
                    {selectedItems.length > 0 ? `${selectedItems.length} selected` : "Select all"}
                  </span>
                </div>
                <span style={{ fontSize: 12, color: "var(--fg-muted)" }}>
                  {filteredInitiatives.length} of {initiatives.length} initiatives
                </span>
              </div>

              {/* Initiative list */}
              <div style={{
                maxHeight: "400px",
                overflowY: "auto",
                padding: "8px 0"
              }}>
                {filteredInitiatives.length === 0 ? (
                  <div style={{ textAlign: "center", padding: "40px 0", color: "var(--fg-muted)" }}>
                    {initiatives.length === 0 ? "No initiatives found in JIRA" : "No initiatives match your filters"}
                  </div>
                ) : (
                  <div style={{ display: "grid", gap: 12 }}>
                    {filteredInitiatives.map((item) => (
                      <div
                        key={item.jira_key}
                        style={{
                          padding: 16,
                          border: "1px solid var(--border)",
                          borderRadius: 8,
                          background: selectedItems.includes(item.jira_key) ? "var(--bg-inset)" : "var(--bg-surface)",
                          cursor: "pointer",
                          transition: "all 0.2s ease"
                        }}
                        onClick={() => handleSelectItem(item.jira_key)}
                      >
                        <div style={{ display: "flex", alignItems: "start", gap: 12 }}>
                          <Checkbox
                            checked={selectedItems.includes(item.jira_key)}
                            onChange={() => {}} // Handled by div click
                          />

                          <div style={{ flex: 1 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                              <h4 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                                {item.title}
                              </h4>
                              <a
                                href={item.external_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{ color: "var(--accent-fg)" }}
                              >
                                <ExternalLink size={12} />
                              </a>
                            </div>

                            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                              <Badge
                                style={{
                                  fontSize: 10,
                                  ...typeBadgeStyle(item.jira_type)
                                }}
                              >
                                {item.jira_type}
                              </Badge>
                              <Badge
                                style={{
                                  fontSize: 10,
                                  ...statusBadgeStyle(item.status)
                                }}
                              >
                                {item.status.replace('_', ' ')}
                              </Badge>
                              <Badge
                                style={{
                                  fontSize: 10,
                                  ...horizonBadgeStyle(item.time_horizon)
                                }}
                              >
                                {item.time_horizon}
                              </Badge>
                              <span style={{ fontSize: 11, color: "var(--fg-muted)" }}>
                                {item.jira_key}
                              </span>
                            </div>

                            {item.description && (
                              <p style={{
                                margin: 0,
                                fontSize: 12,
                                color: "var(--fg-muted)",
                                lineHeight: 1.4,
                                display: "-webkit-box",
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: "vertical",
                                overflow: "hidden"
                              }}>
                                {item.description}
                              </p>
                            )}

                            {item.owner && (
                              <div style={{ marginTop: 8, fontSize: 11, color: "var(--fg-muted)" }}>
                                Owner: {item.owner}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* Actions */}
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          paddingTop: 16,
          borderTop: "1px solid var(--border)"
        }}>
          <div style={{ fontSize: 12, color: "var(--fg-muted)" }}>
            {selectedItems.length > 0 && (
              <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
                <CheckCircle size={14} color="var(--success)" />
                {selectedItems.length} initiative{selectedItems.length !== 1 ? 's' : ''} selected
              </span>
            )}
          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <Button onClick={onClose} disabled={isImporting}>
              Cancel
            </Button>
            <Button
              onClick={handleImport}
              disabled={selectedItems.length === 0 || isImporting}
              style={{
                background: selectedItems.length > 0 ? "var(--success)" : undefined,
                border: "none"
              }}
            >
              {isImporting ? "Importing..." : `Import ${selectedItems.length} Initiative${selectedItems.length !== 1 ? 's' : ''}`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}